import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/internal/operators/take';
import { SharedService } from '../../services/shared.service';
import { UtilsService } from '../../services/utils.service';

interface ScreenshotQueueItem {
	id?: string;
	pageUrl?: string;
	clipId?: string;
	contractType?: string;
	contractId?: string;
	createdAt?: Date;
}

interface ScreenshotQueueDialogData {
	apiService: any;
	showContractType?: boolean;
}

@Component({
	selector: 'app-screenshot-queue-dialog',
	standalone: true,
	imports: [
		CommonModule,
		MatDialogModule,
		MatButtonModule,
		MatIconModule,
		MatTooltipModule,
		MatProgressSpinnerModule,
		MatDividerModule,
		TranslateModule,
	],
	template: `
		<div class="screenshot-queue-container">
			<div class="d-flex justify-content-between align-items-center">
				<span class="title" mat-dialog-title>{{ 'CLIPS.SCREENSHOT_QUEUE' | translate }}</span>
				<button mat-icon-button (click)="closeDialog()" class="d-flex justify-content-center align-items-center me-4">
					<mat-icon>close</mat-icon>
				</button>
			</div>

			<mat-dialog-content class="m-0 p-4">
				@if (loading) {
				<div class="d-flex justify-content-center p-4">
					<mat-spinner diameter="30"></mat-spinner>
				</div>
				} @else if (queueItems.length === 0) {
				<p class="text-center py-3 m-0">{{ 'CLIPS.SCREENSHOT_QUEUE_EMPTY' | translate }}</p>
				} @else {
				<div class="queue-list">
					@for (item of queueItems; track item.id; let i = $index) {
					<div class="queue-item d-flex align-items-center">
						<span class="queue-index">{{ i + 1 }}</span>
						<span class="queue-url flex-fill cursor-pointer" (click)="openUrl(item.pageUrl)" matTooltip="{{ item.pageUrl }}" matTooltipPosition="below">{{ item.pageUrl }}</span>
						@if (data.showContractType && item.contractType) {
						<span class="queue-type ms-3">{{ getContractTypeTranslation(item.contractType) | translate }}</span>
						}
						<span class="queue-date ms-3">{{ utils.formatDate(item.createdAt, true) }}</span>
						<button
							mat-icon-button
							matTooltip="{{ 'BUTTON.DELETE' | translate }}"
							matTooltipPosition="below"
							[disabled]="deletingIds.has(item.id!)"
							(click)="deleteFromQueue(item)">
							@if (deletingIds.has(item.id!)) {
							<mat-spinner diameter="18"></mat-spinner>
							} @else {
							<mat-icon class="delete-icon">close</mat-icon>
							}
						</button>
					</div>
					}
				</div>
				}
			</mat-dialog-content>

			<mat-dialog-actions [align]="'end'">
				<mat-divider class="w-100 my-2"></mat-divider>
				<button mat-flat-button color="primary" (click)="closeDialog()">
					{{ 'BUTTON.CLOSE' | translate }}
				</button>
			</mat-dialog-actions>
		</div>
	`,
	styles: [`
		.screenshot-queue-container {
			background-color: var(--background-color);

			span {
				font-size: small;

				&.title {
					font-size: medium;
				}
			}
		}

		mat-dialog-content {
			max-height: 60vh;
			overflow-y: auto;
		}

		.queue-list {
			display: flex;
			flex-direction: column;
			gap: 6px;
		}

		.queue-item {
			padding: 6px 10px;
			border-radius: 6px;
			background: white;
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
		}

		.queue-index {
			min-width: 22px;
			font-size: 12px !important;
			font-weight: 500;
			color: #999;
			margin-right: 8px;
		}

		.queue-item:hover {
			background: rgba(0, 0, 0, 0.03);
		}

		.queue-url {
			color: var(--primary-color-500);
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			font-size: 13px !important;
		}

		.queue-url:hover {
			text-decoration: underline;
		}

		.queue-type {
			white-space: nowrap;
			color: #999;
			font-size: 12px !important;
			font-weight: 500;
		}

		.queue-date {
			white-space: nowrap;
			color: #999;
			font-size: 12px !important;
		}

		.delete-icon {
			color: #999;
			font-size: 18px;
		}

		.delete-icon:hover {
			color: #f44336;
		}
	`],
})
export class ScreenshotQueueDialogComponent {
	queueItems: ScreenshotQueueItem[] = [];
	loading = true;
	deletingIds = new Set<string>();

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: ScreenshotQueueDialogData,
		private dialogRef: MatDialogRef<ScreenshotQueueDialogComponent>,
		private sharedService: SharedService,
		private translate: TranslateService,
		public utils: UtilsService
	) {
		this.loadQueue();
	}

	closeDialog() {
		this.dialogRef.close();
	}

	loadQueue() {
		this.loading = true;
		this.data.apiService
			.viewScreenshotQueue()
			.pipe(take(1))
			.subscribe({
				next: (response: ScreenshotQueueItem[]) => {
					this.queueItems = response || [];
					this.loading = false;
				},
				error: () => {
					this.queueItems = [];
					this.loading = false;
				},
			});
	}

	deleteFromQueue(item: ScreenshotQueueItem) {
		if (!item.id) return;
		this.deletingIds.add(item.id);
		this.data.apiService
			.deleteFromQueue(item.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.queueItems = this.queueItems.filter(q => q.id !== item.id);
					this.deletingIds.delete(item.id!);
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
						type: 'SUCCESS',
					});
				},
				error: () => {
					this.deletingIds.delete(item.id!);
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	getContractTypeTranslation(contractType: string): string {
		const map: Record<string, string> = {
			CONTRACT_NOTICE: 'CONTRACT_NOTICES.CONTRACT_NOTICE',
			CONTRACT_ANNOUNCEMENT: 'CONTRACT_ANNOUNCEMENTS.CONTRACT_ANNOUNCEMENT',
		};
		return map[contractType] || contractType;
	}

	openUrl(url?: string) {
		if (url) {
			window.open(url, '_blank');
		}
	}
}
